import React, { useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { reduxForm, FieldArray, getFormSyncErrors } from 'redux-form';
import set from 'lodash/set';
import get from 'lodash/get';
import { compose } from 'redux';
import { useSelector } from 'react-redux';
import {
  SelectFieldValueComponent,
  InputFieldValueComponent,
  TextAreaFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { languagesService } from 'nallian-shared-ui/lib/services';
import LanguageSelector from 'nallian-shared-ui/lib/layout/LanguageSelector';
import {
  Field,
  Container,
  Button,
  FormUi,
  Grid,
  Header,
  Icon,
  Divider,
  Flex,
  GrowingContainer,
  Message,
  Link,
} from 'nallian-shared-ui/lib/components';
import { generateErrorsForRequiredFields } from 'nallian-shared-ui/lib/lib/validate';
import lookupService from 'nallian-shared-ui/lib/hoc/lookupService';
import communityService from 'nallian-shared-ui/lib/services/communityService';
import { FormSegment, IndicatorWrapper } from '../../app/styles';
import createJoinRequestService from '../services/createJoinRequestService';
import BasicLayout from '../../app/components/BasicLayout';
import apiRoutes from '../../../app/api/routes';
import { countryOptions } from '../../app/constants/countries';
import CommunityIdentificationCodeTypesService from '../services/communityIdentificationCodeTypesService';
import IdentificationCodeFields from '../../community/members/components/fields/IdentificationCodeFields';
import { validateIdentificationCodes } from '../../community/members/helpers/validateIdentificationCodes';
import { mapJoinRequest } from '../helpers/mapJoinRequest';

const REQUIRED_FIELDS = ['emailAddress', 'fullName', 'language', 'timezone', 'country', 'city'];
const FORM_NAME = 'joinRequest';

const validate = (values, { identificationCodeTypes = [], t }) => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);

  const requiredTypes = identificationCodeTypes
    .filter(type => type.isRequired)
    .map(type => type.value);

  // Extract the types present in the form
  const enteredTypes = (values.identificationCodes || []).map(
    item => item.identificationCodeTypeTag
  );

  // Find missing required types
  const missingTypes = requiredTypes.filter(type => !enteredTypes.includes(type));

  if (missingTypes.length > 0) {
    set(
      errors,
      'identificationCodesMissing',
      t('theFollowingIdentificationCodeTypesAreRequired', { missingTypes: missingTypes.join(', ') })
    );
  }

  validateIdentificationCodes(values?.identificationCodes, errors, identificationCodeTypes, t);

  return errors;
};

const ReduxFormWrapper = reduxForm({
  validate,
  form: FORM_NAME,
  initialValues: {
    freeText: 'Dear administrator, I would like to get access to the following apps: ...',
  },
})(({ children, ...props }) => (typeof children === 'function' ? children(props) : children));

const RequestToJoin = ({
  createJoinRequestFetch,
  dispatchCreateJoinRequestPost,
  timezonesFetch,
  languagesSelectOptions,
  myCommunityFetch,
  dispatchCommunityIdentificationCodeTypesGet,
  communityIdentificationCodeTypesFetch,
}) => {
  const { t } = useTranslation();

  const communityTag = myCommunityFetch.value?.tag;
  const communityId = myCommunityFetch.value?.id;

  const errors = useSelector(state => getFormSyncErrors(FORM_NAME)(state));
  const submitFailed = useSelector(state => get(state, `form.${FORM_NAME}.submitFailed`));

  useEffect(() => {
    if (communityId) dispatchCommunityIdentificationCodeTypesGet(communityId);
  }, [dispatchCommunityIdentificationCodeTypesGet, communityId]);

  const handleSaveJoinRequest = useCallback(
    body => dispatchCreateJoinRequestPost(communityTag, mapJoinRequest(body)),
    [dispatchCreateJoinRequestPost, communityTag]
  );

  const timezoneOptions = useMemo(
    () =>
      (timezonesFetch.value || []).map(tz => ({
        key: tz,
        value: tz,
        text: tz,
      })),
    [timezonesFetch.value]
  );

  const typeOptions = useMemo(
    () =>
      (communityIdentificationCodeTypesFetch.value || []).map(idcode => ({
        key: idcode.type?.tag,
        value: idcode.type?.tag,
        text: idcode.type?.name,
        validationPattern: idcode.validationPattern,
        isRequired: idcode.isRequired,
      })),
    [communityIdentificationCodeTypesFetch.value]
  );

  if (createJoinRequestFetch.rejected) {
    return (
      <BasicLayout>
        <FormSegment>
          <GrowingContainer verticalAlign="space-between">
            <Message
              grow
              basic
              error
              icon="mail"
              header={t('joinRequestError')}
              content={t('joinRequestErrorMessage')}
              size="huge"
              margin="5rem 1rem"
            />
          </GrowingContainer>
        </FormSegment>
      </BasicLayout>
    );
  }

  if (createJoinRequestFetch.fulfilled) {
    return (
      <BasicLayout>
        <FormSegment>
          <GrowingContainer verticalAlign="space-between">
            <Message
              grow
              basic
              success
              icon="mail"
              header={t('joinRequestSentToCommunityAdmin')}
              content={t('joinRequestSentToCommunityAdminDescription')}
              size="huge"
              margin="5rem 1rem"
            />
          </GrowingContainer>
        </FormSegment>
      </BasicLayout>
    );
  }

  return (
    <ReduxFormWrapper identificationCodeTypes={typeOptions} t={t}>
      {({ handleSubmit }) => (
        <BasicLayout>
          <LanguageSelector isPublic />
          <FormSegment>
            <IndicatorWrapper>
              <Flex fluid direction="column">
                <Header as="h2">
                  <Icon name="id card" />
                  <Header.Content>{t('requestToJoin')}</Header.Content>
                </Header>
                <p>
                  <Trans i18nKey="requestToJoinInfo" components={{ bold: <strong /> }} />
                </p>
                <Divider />
              </Flex>
            </IndicatorWrapper>
            <FormUi>
              <Grid>
                <Grid.Column width="16">
                  <Field
                    fullwidth
                    label={t('organisationName')}
                    name="organisationName"
                    valueComponent={InputFieldValueComponent}
                  />
                </Grid.Column>
                <Grid.Column width="8">
                  <Field
                    fullwidth
                    label={t('city')}
                    type="text"
                    name="city"
                    valueComponent={InputFieldValueComponent}
                  />
                </Grid.Column>
                <Grid.Column width="8">
                  <Field
                    search
                    clearable
                    fluid
                    name="country"
                    label={t('country')}
                    valueComponent={SelectFieldValueComponent}
                    options={countryOptions}
                  />
                </Grid.Column>
                {typeOptions?.length > 0 && (
                  <Grid.Column mobile={16} tablet={16} computer={16}>
                    <FieldArray
                      name="identificationCodes"
                      component={IdentificationCodeFields}
                      identificationCodeTypes={typeOptions}
                      prefillRequiredOptions
                    />
                  </Grid.Column>
                )}
                <Grid.Column width="16">
                  <Divider />
                </Grid.Column>
                <Grid.Column width="16">
                  <Field
                    fullwidth
                    label={t('fullName')}
                    name="fullName"
                    valueComponent={InputFieldValueComponent}
                  />
                </Grid.Column>
                <Grid.Column width="8">
                  <Field
                    fullwidth
                    label={t('eMailaddress')}
                    type="email"
                    name="emailAddress"
                    valueComponent={InputFieldValueComponent}
                  />
                </Grid.Column>
                <Grid.Column width="8">
                  <Field
                    fullwidth
                    label={t('phoneNumber')}
                    type="text"
                    name="phoneNumber"
                    valueComponent={InputFieldValueComponent}
                  />
                </Grid.Column>
                <Grid.Column width="8">
                  <Field
                    search
                    clearable
                    fluid
                    name="language"
                    label={t('language')}
                    valueComponent={SelectFieldValueComponent}
                    options={languagesSelectOptions}
                  />
                </Grid.Column>
                <Grid.Column width="8">
                  <Field
                    search
                    fluid
                    clearable
                    label={t('timezone')}
                    name="timezone"
                    valueComponent={SelectFieldValueComponent}
                    options={timezoneOptions}
                  />
                </Grid.Column>
                <Grid.Column width="16">
                  <Field
                    search
                    fluid
                    clearable
                    rows={5}
                    label={t('joinMessage')}
                    name="freeText"
                    valueComponent={TextAreaFieldValueComponent}
                  />
                </Grid.Column>
              </Grid>
              {submitFailed && errors?.identificationCodesMissing && (
                <Message error className="visible" content={errors?.identificationCodesMissing} />
              )}
              <Container textAlign="right">
                <Link to="/">
                  <Button content={t('back')} disabled={createJoinRequestFetch.pending} />
                </Link>
                <Button
                  content={t('requestToJoin')}
                  onClick={handleSubmit(handleSaveJoinRequest)}
                  disabled={createJoinRequestFetch.pending}
                  loading={createJoinRequestFetch.pending}
                  btnprimary
                />
              </Container>
            </FormUi>
          </FormSegment>
        </BasicLayout>
      )}
    </ReduxFormWrapper>
  );
};

RequestToJoin.propTypes = {
  timezonesFetch: PropTypes.object.isRequired,
  myCommunityFetch: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dispatchCreateJoinRequestPost: PropTypes.func.isRequired,
  createJoinRequestFetch: PropTypes.object.isRequired,
  dispatchCommunityIdentificationCodeTypesGet: PropTypes.func.isRequired,
  communityIdentificationCodeTypesFetch: PropTypes.object.isRequired,
  languagesSelectOptions: PropTypes.array,
};

RequestToJoin.defaultProps = {
  languagesSelectOptions: [],
};

export default compose(
  createJoinRequestService,
  communityService,
  CommunityIdentificationCodeTypesService,
  lookupService({ resource: 'timezones', url: apiRoutes.getTimezones() }),
  languagesService
)(RequestToJoin);
